<template>
  <div class="pdl-datepicker form-group--inline" :qaid="qaid">
    <span v-if="allowIcon" class="form-label">
      <pdl-icon :name="enableTime ? 'access_time' : 'date_range'" />
    </span>
    <input
      :id="inputId"
      ref="picker"
      class="form-control flatpickr"
      type="text"
      :aria-label="label"
      :name="name"
      :placeholder="placeholder"
      data-input
    />
  </div>
</template>

<script>
import Flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import {PdlIcon} from '@pedal/pdl-icon';

const includedEvents = ['onChange', 'onClose', 'onDestroy', 'onKeyDown', 'onMonthChange', 'onOpen', 'onYearChange'];
const excludedEvents = ['onValueUpdate', 'onDayCreate', 'onParseConfig', 'onReady', 'onPreCalendarPosition'];

const allEvents = includedEvents.concat(excludedEvents);
const configCallbacks = ['locale', 'showMonths'];
const camelToKebab = (string) => {
  return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};
const toArray = (obj) => {
  return obj instanceof Array ? obj : [obj];
};

export default {
  name: 'PdlDatePicker',
  components: {PdlIcon},
  props: {
    /**
     * Display of decorative leading icon.
     */
    allowIcon: {
      type: Boolean,
      default: true,
    },
    /**
     * Allow a date to be entered directly into the input field.
     */
    allowInput: {
      type: Boolean,
      default: false,
    },
    /**
     * Allows the preloading of an invalid date. When disabled, the field will be cleared if the provided date is invalid.
     */
    allowInvalidPreload: {
      type: Boolean,
      default: false,
    },
    /**
     * Exactly the same as `dateFormat`, but for the `altInput` field.
     */
    altFormat: {
      type: String,
      default: 'M j, Y',
    },
    /**
     * Hides the original input and creates a new one. Upon date selection, the original input will contain a `Y-m-d...` string, while the `altInput` will display the date in a more legible, customizable format. Enabling this option is highly recommended.
     */
    altInput: {
      type: Boolean,
      default: true,
    },
    /**
     * Instead of `body`, appends the calendar to the specified node instead.
     */
    appendTo: {
      type: Object,
      default: null,
    },
    /**
     * A string of characters which are used to define how the date will be displayed in the input box. The supported characters are defined in the <a href="https://flatpickr.js.org/formatting/" target="_blank">Formatting Tokens</a>.
     */
    dateFormat: {
      type: String,
      default: 'Y-m-d',
    },
    /**
     * Sets the initial selected date(s).
     *
     * If you're using `mode: "multiple"` or a range calendar supply an `Array` of `Date` objects or an Array of date strings which follow your `dateFormat`.
     *
     * Otherwise, you can supply a single Date object or a date string.
     */
    defaultDate: {
      type: [Date, String, Array],
      default: null,
    },
    /**
     * Enables time picker.
     */
    enableTime: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => includedEvents,
    },
    /**
     * Display the calendar in an always-open state.
     */
    inline: {
      type: Boolean,
      default: false,
    },
    /**
     * ID of input field.
     */
    inputId: {
      type: String,
      default: '',
    },
    /**
     * Disable date picker.
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * The maximum date that a user can pick to (inclusive).
     */
    maxDate: {
      type: [Date, String],
      default: null,
    },
    /**
     * The minimum date that a user can start picking from (inclusive).
     */
    minDate: {
      type: [Date, String],
      default: null,
    },
    /**
     * Adjusts the step for the minute input.
     */
    minuteIncrement: {
      type: Number,
      default: 30,
    },
    /**
     * Date selection mode (`single`, `multiple`, or `range`).
     */
    mode: {
      type: String,
      default: 'single',
    },
    /**
     * Name of input field.
     */
    name: {
      type: String,
      default: '',
    },
    /**
     * HTML for the right arrow icon, used to switch months.
     */
    nextArrow: {
      type: String,
      default: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M5.88 4.12L13.76 12l-7.88 7.88L8 22l10-10L8 2z"/></svg>`,
    },
    /**
     * Hides the day selection in calendar. Use it along with `enableTime` to create a time picker.
     */
    noCalendar: {
      type: Boolean,
      default: false,
    },
    /**
     * Placeholder text for input field.
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * HTML for the left arrow icon, used to switch months.
     */
    prevArrow: {
      type: String,
      default: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M11.67 3.87L9.9 2.1 0 12l9.9 9.9 1.77-1.77L3.54 12z"/></svg>`,
    },
    /**
     * A `qaid` is used as a unique string, serving as a hook for automated tests.
     */
    qaid: {
      type: String,
      default: 'date-picker',
    },
    /**
     * Text will be added to aria-label attribute.
     */
    label: {
      type: String,
      default: null,
    },
    modelValue: {
      default: null,
      validator(value) {
        return (
          value === null ||
          value instanceof Date ||
          typeof value === 'string' ||
          value instanceof String ||
          value instanceof Array ||
          typeof value === 'number'
        );
      },
    },
  },
  data() {
    return {
      // The FlatPicker instance
      fp: null,
      config: {
        mode: this.mode,
        allowInput: this.allowInput,
        allowInvalidPreload: this.allowInvalidPreload,
        inline: this.inline,
        altInput: this.altInput,
        altFormat: this.altFormat,
        dateFormat: this.dateFormat,
        appendTo: this.appendTo,
        defaultDate: this.normalizeDate(this.defaultDate),
        nextArrow: this.nextArrow,
        prevArrow: this.prevArrow,
        enableTime: this.enableTime,
        noCalendar: this.noCalendar,
        minuteIncrement: this.minuteIncrement,
        minDate: this.normalizeDate(this.minDate),
        maxDate: this.normalizeDate(this.maxDate),
        onChange: this.onChange,
        ...(this.allowInput && this.altInput ? {onClose: this.onClose} : {}),
      },
    };
  },
  watch: {
    config: {
      deep: true,
      handler(newConfig) {
        const config = this.sanitizeConfig({...newConfig});

        // Workaround: Don't pass hooks to configs again otherwise
        // previously registered hooks will stop working
        // Notice: we are looping through all events
        // This also means that new callbacks can not passed once component has been initialized
        allEvents.forEach((hook) => {
          delete config[hook];
        });

        this.fp.set(config);

        // Workaround: Allow to change locale dynamically
        configCallbacks.forEach((name) => {
          if (typeof config[name] !== 'undefined') {
            this.fp.set(name, config[name]);
          }
        });
      },
    },
    modelValue(newValue) {
      if (newValue === this.$el.value) return;

      this.fp?.setDate(newValue, true);
    },
    isDisabled: function (newValue) {
      this.$refs.picker.disabled = newValue;
      if (!newValue) {
        this.initFlatpickr();
      } else {
        this.$refs.picker.value = null;
        this.fpInput().removeEventListener('blur', this.onBlur);
        this.fp.destroy();
        this.fp = null;
      }
    },
    label(newLabel) {
      if (this.fp?.altInput) {
        if (newLabel) {
          this.fp.altInput.setAttribute('aria-label', newLabel);
        } else {
          this.fp.altInput.removeAttribute('aria-label');
        }
      }
    },
  },
  mounted() {
    if (this.fp) return;
    if (this.isDisabled) {
      this.$refs.picker.disabled = this.isDisabled;
    } else {
      this.initFlatpickr();
    }
  },

  beforeUnmount() {
    if (this.fp) {
      this.fpInput().removeEventListener('blur', this.onBlur);
      this.fp.destroy();
      this.fp = null;
    }
  },
  methods: {
    sanitizeConfig(config) {
      return Object.entries(config)
        .filter((entry) => entry[1])
        .reduce((obj, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {});
    },
    initFlatpickr() {
      const config = this.sanitizeConfig({...this.config});

      this.events.forEach((hook) => {
        config[hook] = toArray(config[hook] || []).concat((...args) => {
          this.$emit(camelToKebab(hook), ...args);
        });
      });

      config.defaultDate = this.normalizeDate(this.modelValue || config.defaultDate);
      this.fp = new Flatpickr(this.$refs.picker, config);

      // copy data attributes from input to flatpickr input to allow scoped styles to style the flatpickr input
      this.fp._input.setAttribute(this.$options._scopeId, '');
      this.fp?.calendarContainer?.setAttribute(this.$options._scopeId, '');

      this.fpInput().addEventListener('blur', this.onBlur);
      this.fp?.calendarContainer?.classList.add('pdl-datepicker', 'text-sm');
    },
    onChange(selectedDates, dateStr) {
      if (this.mode === 'range') {
        const [startDate, endDate] = selectedDates;
        this.$emit('update:modelValue', {
          event: dateStr,
          startDate,
          endDate,
        });
      } else {
        /**
         * Emitted when when the user selects a date, or changes the time on a selected date.
         */
        this.$emit('update:modelValue', {
          event: dateStr,
        });
      }
    },
    onClose() {
      if (this.fpInput().value !== '') return;
      this.fp.setDate(this.fpInput().value, true);
    },
    normalizeDate(date) {
      if (date instanceof Date && !this.enableTime) {
        const updatedDate = date.toISOString().slice(0, 10);
        return updatedDate;
      } else {
        return date;
      }
    },
    fpInput() {
      return this.fp.altInput || this.fp.input;
    },

    onBlur(event) {
      /**
       * Emitted when input loses focus.
       */
      this.$emit('blur', event.target.value);
    },
  },
};
</script>
<style lang="scss">
@import 'Forms.scss';
@import 'DatePicker.scss';
</style>
